import { useEffect, useState } from 'react';
import { commandsApi, nodeAuthApi, nodesApi, sitesApi } from '@verlo/shared/services/api';
import { Button } from '@verlo/shared/components/ui/button';
import { Segmented } from '@verlo/shared/components/ui/segmented';
import { Signal } from '@verlo/shared/components/ui/signal';
import { Notice } from '@verlo/shared/components/ui/notice';
import { RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { PageHeader, Stat, Stats, Loading, Empty, Panel as Section } from '../../components/office';

// Verlo Node (Back office sweep, Oct 2026). The box in the restaurant that runs the tills when
// the internet doesn't, and keeps them in step with the office: how it is now, this till's
// own settings, signing in while offline, and booking edits it couldn't take.

// Liveness is derived server-side (`heartbeat.liveness`) from the node's last heartbeat.
const LIVENESS = {
  online: { label: 'online', tone: 'ok' },
  stale: { label: 'quiet', tone: 'warn' },
  offline: { label: 'offline', tone: 'stop' },
  never: { label: 'no word yet', tone: 'info' },
  pending: { label: 'pending', tone: 'info' },
  revoked: { label: 'revoked', tone: 'stop' },
};

const formatRelative = (iso) => {
  if (!iso) return '—';
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 0) return 'just now';
  if (diff < 60) return `${Math.floor(diff)}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
};

const formatUptime = (seconds) => {
  if (seconds == null) return '—';
  if (seconds < 60) return `${Math.floor(seconds)}s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h`;
  return `${Math.floor(seconds / 86400)}d`;
};

const formatDisk = (mb) => {
  if (mb == null) return '—';
  if (mb >= 1024) return `${(mb / 1024).toFixed(1)} GB`;
  return `${mb} MB`;
};

const orNa = (v) => (v == null ? '—' : v);

export const VerloNodePage = () => {
  const [status, setStatus] = useState(null); // { site_id, registered, node }
  const [loading, setLoading] = useState(true);

  // ---- Phase C1: offline bundle (node_auth path, separate from telemetry) ----
  const [bundle, setBundle] = useState(null);
  const [bundleBusy, setBundleBusy] = useState(false);
  const [siteId, setSiteId] = useState('');

  useEffect(() => {
    sitesApi
      .list()
      .then((r) => setSiteId(r.data?.[0]?.id || ''))
      .catch(() => {});
  }, []);

  // Quiet when the page opens; it says so when someone presses Refresh.
  const pullBundle = async (say = true) => {
    setBundleBusy(true);
    try {
      const r = await nodeAuthApi.getBundle(siteId);
      setBundle(r.data);
      if (say) toast.success(`${r.data.count} people can sign in while the internet's down`);
    } catch (e) {
      if (say) toast.error(e.response?.data?.detail || "Couldn't refresh who can sign in offline");
    } finally {
      setBundleBusy(false);
    }
  };

  // Reservation-handoff exceptions (S4-6): the commands a node refused — a cloud-authored edit
  // whose base revision no longer matched because a door edit won (§3.4, node-wins + flag).
  const [exceptions, setExceptions] = useState([]);

  const fetchStatus = async () => {
    try {
      const res = await nodesApi.getStatus();
      setStatus(res.data);
    } catch {
      toast.error('Could not load Node status');
    } finally {
      setLoading(false);
    }
  };

  const fetchExceptions = async () => {
    try {
      const res = await commandsApi.listExceptions();
      setExceptions(res.data || []);
    } catch {
      // A manager without floor.view, or an offline read: leave the list empty rather than error.
    }
  };

  useEffect(() => {
    fetchStatus();
    fetchExceptions();
    const t = setInterval(() => { fetchStatus(); fetchExceptions(); }, 15000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    pullBundle(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const header = (
    <PageHeader
      title="Verlo Node"
      purpose="The box in your restaurant that runs the tills when the internet doesn't, and keeps them in step with the office."
      actions={<Button variant="outline" onClick={fetchStatus} data-testid="node-refresh-btn"><RefreshCw size={14} className="mr-1.5" />Refresh</Button>}
    />
  );
  if (loading || !status) return <div className="space-y-5">{header}<Loading /></div>;

  const node = status.node;
  const live = LIVENESS[node?.liveness] || LIVENESS.never;
  const dead = node?.dead_letter_depth || 0;
  const backlog = node?.queue_depth || 0;

  return (
    <div className="space-y-5" data-testid="node-page">
      {header}

      {!status.registered ? (
        <Empty word="Node" title="No node registered for this site" testId="node-empty-card">
          This site has no Verlo Node yet. Verlo registers one and sets the box up; once it starts, it reports here every few seconds.
        </Empty>
      ) : (
        <>
          <section className="rounded-lg border border-hairline bg-white px-5 py-4" data-testid="node-hero-card">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
              <h2 className="font-heading text-[18px] font-extrabold text-ink">{node.name || 'Verlo Node'}</h2>
              <Signal tone={live.tone} data-testid="node-liveness-label">{live.label}</Signal>
              <span className="font-mono text-[12px] text-ink-faint">{node.id}</span>
            </div>
            <p className="mt-1 text-[13px] text-ink-soft">
              Last heard from <b className="font-medium text-ink" data-testid="node-last-seen">{formatRelative(node.last_seen_at)}</b>; running for {formatUptime(node.uptime_seconds)} since its last restart, on {orNa(node.app_version)}.
            </p>
          </section>

          {dead > 0 && (
            <Notice tone="stop" word="Sync" title={`${dead} change${dead === 1 ? '' : 's'} the office refused`}>
              The till sent them and the office couldn't take them. Verlo support can see which, and why.
            </Notice>
          )}

          <Stats>
            <Stat label="Waiting to sync" value={orNa(node.queue_depth)} testId="node-queue-depth" delta={backlog > 0 ? { text: 'Sends when the line is back', tone: 'warn' } : undefined} hint={backlog ? undefined : 'All caught up'} />
            <Stat label="Refused" value={orNa(node.dead_letter_depth)} testId="node-deadletter-depth" delta={dead ? { text: 'Needs a look', tone: 'stop' } : undefined} hint={dead ? undefined : 'None'} />
            <Stat label="Disk free" value={formatDisk(node.disk_free_mb)} hint="On the box's data drive" />
            <Stat label="On the network" value={orNa(node.lan_client_count)} hint="Tills and printers it can see" />
          </Stats>

          {/* This till's own settings (modifiers plan): what a manager can flip for one Pi.
              They ride back to the till on its next heartbeat (~15 s). */}
          <Section title="This till" testId="node-settings-card"
            purpose="Settings for this till alone. Each reaches it on its next check-in, within a few seconds.">
            <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-4">
              <div className="min-w-0">
                <div className="text-[14px] text-ink">Priced free text</div>
                <div className="text-[12.5px] text-ink-soft">
                  {node.settings?.free_text_pricing == null
                    ? 'Follows the site (Admin › Company › Tills).'
                    : node.settings.free_text_pricing ? 'On for this till, whatever the site says. Every priced entry is on the Typed extras report.' : 'Off for this till, whatever the site says.'}
                </div>
              </div>
              <Segmented
                value={node.settings?.free_text_pricing == null ? 'site' : node.settings.free_text_pricing ? 'on' : 'off'}
                onChange={async (v) => {
                  try {
                    await nodesApi.patchSettings(node.id, v === 'site' ? { clear: ['free_text_pricing'] } : { free_text_pricing: v === 'on' });
                    toast.success(v === 'site' ? 'This till follows the site' : `Priced free text ${v} for this till`);
                    fetchStatus();
                  } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't save"); }
                }}
                options={[{ value: 'site', label: 'As the site', testId: 'node-freetext-site' }, { value: 'on', label: 'On', testId: 'node-freetext-on' }, { value: 'off', label: 'Off', testId: 'node-freetext-off' }]}
              />
            </div>
          </Section>
        </>
      )}

      {/* ---- Phase C1: Offline Auth Cache (node_auth path) ----------------------------- */}
      {/* An "Audit batch queue" sat beside this: a till's queue simulated in the browser, with
          Sim buttons, posting to /node/audit/batch, which takes a node's token and so refused
          every flush. A real node sends its own; the rows land in the Audit log. */}
      <Section title="Signing in when the internet's down" testId="node-offline-auth-card"
        purpose="The till keeps a copy of everyone's PIN, scrambled, so the team can still sign in offline. A PIN can't be changed until it's back online."
        right={<Button variant="outline" size="sm" onClick={() => pullBundle()} disabled={bundleBusy} data-testid="node-bundle-refresh-btn">
          <RefreshCw size={13} className={`mr-1.5 ${bundleBusy ? 'animate-spin' : ''}`} />{bundleBusy ? 'Refreshing…' : 'Refresh'}</Button>}>
        <div className="px-5 py-4 text-[13.5px]">
          {!bundle ? <span className="text-ink-faint">Not fetched yet.</span> : (
            <span className="text-ink-soft">
              <b className="font-mono font-semibold text-ink" data-testid="node-bundle-count">{bundle.count}</b> people, as of {formatRelative(bundle.issued_at)}
              <span className="ml-2 font-mono text-[12px] text-ink-faint" data-testid="node-bundle-fp" title="Fingerprint of the copy">{bundle.fingerprint}</span>
            </span>
          )}
        </div>
      </Section>

      {/* Reservation handoff exceptions (S4-6, §3.4) — the commands a node refused. */}
      <Section title="Booking changes the till didn't take" testId="node-exceptions-card"
        purpose="Changes made here to a booking that someone at the door had already changed. The door's change stands; make these again by hand if they still matter.">
        {exceptions.length === 0 ? (
          <p className="px-5 py-4 text-[13.5px] text-ink-soft" data-testid="node-exceptions-empty">None. Every booking change reached the till.</p>
        ) : (
          <div className="divide-y divide-hairline" data-testid="node-exceptions-list">
            {exceptions.map((exc) => (
              <div key={exc.id} className="flex items-start justify-between gap-4 px-5 py-3 text-[13.5px]">
                <div className="min-w-0">
                  <div className="truncate font-medium text-ink">{exc.payload?.reservation?.customer_name || exc.payload?.reservation_id || exc.type}</div>
                  <div className="text-[12.5px] text-ink-soft">{exc.reason || exc.outcome}</div>
                </div>
                <div className="shrink-0 text-[12px] text-ink-faint">{formatRelative(exc.created_at)}</div>
              </div>
            ))}
          </div>
        )}
      </Section>
    </div>
  );
};

export default VerloNodePage;
